import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '@/api';

/**
 * /attachments/:id/open — the target of the "Attachment N" links in the Excel
 * export.
 *
 * A spreadsheet outlives any storage URL: a private one would not open, and a
 * pre-signed one expires within days and opens for anyone the file is
 * forwarded to. So the export links here instead. This page sits behind login
 * (the router returns you here afterwards), asks the server for a short-lived
 * link — which applies the same access rules as the rest of the DMS — and
 * hands the browser over to it.
 */
const AttachmentOpenPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<'opening' | 'unavailable' | 'error'>('opening');
  const [filename, setFilename] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!id || !/^[0-9a-fA-F]{24}$/.test(id)) {
        setState('unavailable');
        return;
      }
      try {
        const res: any = await apiClient.attachments.getPresignedUrl(id, { inline: true });
        const data = res?.data || res || {};
        if (cancelled) return;
        if (data.filename) setFilename(data.filename);
        if (data.presignedUrl) {
          // replace: Back returns to wherever the link was clicked from
          window.location.replace(data.presignedUrl);
          return;
        }
        // No short-lived link available: fetch the bytes and open them here.
        const blob = await apiClient.attachments.download(id);
        if (cancelled) return;
        window.location.replace(URL.createObjectURL(blob));
      } catch (err: any) {
        if (cancelled) return;
        const status = err?.response?.status;
        setState(status === 404 || status === 403 ? 'unavailable' : 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="max-w-md text-center">
        {state === 'opening' && (
          <>
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
            <p className="text-gray-700 dark:text-gray-300">
              Opening{filename ? ` ${filename}` : ' the document'}…
            </p>
          </>
        )}
        {state === 'unavailable' && (
          <>
            <h1 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">This file isn't available</h1>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              It may have been removed, or it belongs to a project you don't have access to.
            </p>
            <Link to="/correspondences" className="text-sm text-blue-600 hover:underline dark:text-blue-400">
              Go to correspondences
            </Link>
          </>
        )}
        {state === 'error' && (
          <>
            <h1 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">Couldn't open the file</h1>
            <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
              Something went wrong fetching it. Please try the link again in a moment.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="text-sm text-blue-600 hover:underline dark:text-blue-400"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AttachmentOpenPage;
